import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Info, Search } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useLanguage } from '../hooks/useLanguage';

const MAXIM_RANGES = [
  [1905, 1, 200], [1906, 200, 320], [1907, 431, 757], [1908, 980, 1611], [1909, 1702, 2350],
  [1910, 2350, 2900], [1911, 2900, 3300], [1912, 3300, 4500], [1913, 4500, 5200], [1914, 5200, 6283],
  [1915, 6284, 11599], [1916, 11600, 23799], [1917, 23800, 34999], [1918, 35000, 47402], [1919, 47403, 47699],
  [1920, 47700, 50999], [1921, 51000, 52999], [1922, 53000, 53999], [1923, 54000, 54999], [1924, 55000, 56491],
  [1925, 56492, 58499], [1926, 58500, 59329], [1927, 59330, 60985], [1928, 60986, 61765], [1929, 61766, 66578],
  [1930, 66579, 69999], [1931, 70000, 78826], [1932, 78827, 95599], [1933, 95600, 102614], [1934, 102615, 109400],
].map(([year, min, max]) => ({ year, min, max }));

const TOZ_RANGES = [
  [1911, 635, 792], [1912, 1168, 1245], [1913, 1543, 1682], [1914, 1928, 1928], [1915, 3060, 4330],
  [1916, 5691, 12462], [1917, 13743, 18033], [1918, 18988, 20226], [1919, 20940, 26758], [1920, 30052, 30052],
  [1921, 31936, 31938], [1922, 35192, 35192], [1923, 37408, 37481], [1927, 40538, 41880], [1928, 42872, 43615],
  [1929, 44771, 45021], [1930, 47851, 52747], [1931, 52853, 59675], [1932, 68531, 68902], [1933, 75544, 75544],
  [1934, 83400, 83400],
].map(([year, min, max]) => ({ year, min, max }));

const EMPTY_RANGES = [];

const copy = {
  RU: {
    kicker: 'ИНСТРУМЕНТ', title: 'Определение года по серийному номеру', intro: 'Введите номер, чтобы сопоставить его с известными экземплярами. Результат является ориентиром, а не заводским подтверждением.',
    maxim: 'Пулемёт Максима', toz: 'Станок ТОЗ', orz: 'Станок ОРЗ', label: 'Введите серийный номер', placeholder: 'Например, 9033', noResult: 'Введите номер для расчёта', exact: 'Известное совпадение', likely: 'Вероятный год', between: 'Вероятный период', before: 'Ранее', after: 'Позднее', range: 'Известный диапазон', nearest: 'Ближайшие известные номера', records: 'Показать записи за этот год', timeline: 'Опорные диапазоны', orzTimeline: 'Хронология станков ОРЗ', note: 'Диапазоны собраны по известным экземплярам и производственным данным. Пограничные номера и повторное начало нумерации могут давать неоднозначный результат.', orzNote: 'Для ОРЗ показаны номера изделий и деталей с известным годом выпуска. Повторы в пределах года объединены. Разные детали одного станка могут иметь соседние годы, поэтому вывод делается по ближайшим точкам.', resetNote: 'Для небольших номеров учитывайте возможное обнуление нумерации в 1935–1937 годах.', serialShort: '№', loadingOrz: 'Загрузка данных ОРЗ…', errorOrz: 'Не удалось загрузить данные ОРЗ.', noOrz: 'В базе пока нет номерных изделий или деталей ОРЗ с известным годом выпуска.',
  },
  EN: {
    kicker: 'TOOL', title: 'Estimate a production year by serial number', intro: 'Enter a serial number to compare it with documented examples. The result is a guide, not a factory certificate.',
    maxim: 'Maxim gun', toz: 'TOZ mount', orz: 'ORZ mount', label: 'Enter a serial number', placeholder: 'For example, 9033', noResult: 'Enter a number to calculate', exact: 'Documented match', likely: 'Likely year', between: 'Likely period', before: 'Earlier than', after: 'Later than', range: 'Documented range', nearest: 'Nearest documented numbers', records: 'View records from this year', timeline: 'Reference ranges', orzTimeline: 'ORZ mount chronology', note: 'Ranges are compiled from documented examples and production data. Boundary numbers and numbering restarts may produce an ambiguous result.', orzNote: 'The ORZ view lists serial numbers from complete mounts and parts with a known production year. Repeated numbers are combined within each year. Parts from one mount may carry adjacent years, so the estimate is based on nearby evidence.', resetNote: 'For small numbers, allow for numbering restarts in 1935–1937.', serialShort: 'No.', loadingOrz: 'Loading ORZ data…', errorOrz: 'Unable to load ORZ data.', noOrz: 'No numbered ORZ mounts or parts with a known production year are recorded yet.',
  },
};

function serialNumber(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits ? Number.parseInt(digits, 10) : Number.NaN;
}

function calculateRange(number, ranges) {
  if (!Number.isFinite(number) || !ranges.length) return null;

  for (let index = 0; index < ranges.length; index += 1) {
    const current = ranges[index];
    const next = ranges[index + 1];
    if (number >= current.min && number <= current.max) return { kind: 'likely', years: [current.year], record: current };
    if (next && number > current.max && number < next.min) return { kind: 'between', years: [current.year, next.year], record: current };
  }
  if (number < ranges[0].min) return { kind: 'before', years: [ranges[0].year] };
  return { kind: 'after', years: [ranges[ranges.length - 1].year] };
}

function calculateFromPoints(number, points) {
  if (!Number.isFinite(number) || !points.length) return null;
  const exact = points.filter((point) => point.number === number);
  if (exact.length) {
    const years = [...new Set(exact.map((point) => point.year))].sort((a, b) => a - b);
    return { kind: 'exact', years, evidence: exact };
  }

  const lower = [...points].reverse().find((point) => point.number < number);
  const upper = points.find((point) => point.number > number);
  if (!lower) return { kind: 'before', years: [points[0].year], evidence: [points[0]] };
  if (!upper) return { kind: 'after', years: [points[points.length - 1].year], evidence: [points[points.length - 1]] };
  const years = [...new Set([lower.year, upper.year])].sort((a, b) => a - b);
  return { kind: years.length > 1 ? 'between' : 'likely', years, evidence: [lower, upper] };
}

function groupOrzRecords(records) {
  const groups = new Map();
  records.forEach((record) => {
    const number = serialNumber(record.number);
    const year = Number(record.year);
    if (!Number.isFinite(number) || !Number.isInteger(year) || year <= 0) return;
    if (!groups.has(year)) groups.set(year, new Set());
    groups.get(year).add(number);
  });
  const grouped = [...groups.entries()]
    .map(([year, numbers]) => ({ year, numbers: [...numbers].sort((a, b) => a - b) }))
    .sort((a, b) => a.year - b.year);
  const points = grouped.flatMap(({ year, numbers }) => numbers.map((number) => ({ number, year })));
  return { grouped, points: points.sort((a, b) => a.number - b.number || a.year - b.year) };
}

function responseRecords(data) {
  return Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
}

async function fetchOrzProducts() {
  const records = [];
  let page = 0;
  let totalPages = 1;
  do {
    const { data } = await api.get('/products/search', {
      params: { q: '', belongsTo: 'станок', manufacturer: 'ОР.З.', page, size: 1000, sortField: 'id', sortOrder: 'asc' },
    });
    records.push(...responseRecords(data));
    totalPages = data?.totalPages || 1;
    page += 1;
  } while (page < totalPages);
  return records;
}

export default function GuessYearPage() {
  const { lang } = useLanguage();
  const labels = copy[lang] || copy.RU;
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedKind = searchParams.get('kind');
  const kind = requestedKind === 'toz' || requestedKind === 'orz' ? requestedKind : 'maxim';
  const [input, setInput] = useState('');
  const [orzRecords, setOrzRecords] = useState([]);
  const [orzStatus, setOrzStatus] = useState('loading');

  const setKind = (value) => {
    if (value === kind) return;
    const next = new URLSearchParams(searchParams);
    if (value === 'maxim') next.delete('kind');
    else next.set('kind', value);
    // Keep the selected tab on this history entry when returning from the archive.
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    let active = true;
    Promise.all([
      api.get('/details/by-manufacturer-and-belongs-to', {
        params: { belongsTo: 'станок', manufacturerId: 1 },
      }),
      fetchOrzProducts(),
    ])
      .then(([{ data }, products]) => {
        if (!active) return;
        setOrzRecords([...responseRecords(data), ...products]);
        setOrzStatus('ready');
      })
      .catch(() => active && setOrzStatus('error'));
    return () => { active = false; };
  }, []);

  const orzData = useMemo(() => groupOrzRecords(orzRecords), [orzRecords]);
  const ranges = kind === 'maxim' ? MAXIM_RANGES : kind === 'toz' ? TOZ_RANGES : EMPTY_RANGES;
  const belongsTo = kind === 'maxim' ? 'максим' : 'станок';
  const manufacturer = kind === 'orz' ? 'ОР.З.' : '';
  const number = input ? Number.parseInt(input, 10) : Number.NaN;
  const result = useMemo(
    () => kind === 'orz'
      ? calculateFromPoints(number, orzData.points)
      : calculateRange(number, ranges),
    [kind, number, orzData.points, ranges],
  );

  const resultTitle = result
    ? result.kind === 'exact' ? labels.exact
      : result.kind === 'likely' ? labels.likely
        : result.kind === 'between' ? labels.between
          : result.kind === 'before' ? labels.before
            : labels.after
    : kind === 'orz' && orzStatus === 'loading' ? labels.loadingOrz : labels.noResult;
  const resultValue = result ? result.years.join('–') : '—';
  const selectedYears = result?.years || [];
  const primaryYear = selectedYears[0];
  const searchUrl = primaryYear
    ? '/search?' + new URLSearchParams({ searchType: 'all', belongsTo, ...(manufacturer && { manufacturer }), year: String(primaryYear) }).toString()
    : null;

  return (
    <main className="year-tool page-width">
      <header className="tool-header"><p className="section-kicker">{labels.kicker}</p><h1>{labels.title}</h1><p>{labels.intro}</p></header>

      <section className={Number.isFinite(number) ? 'year-tool__workspace' : 'year-tool__workspace is-awaiting-input'}>
        <div className="year-tool__control">
          <div className="year-kind" role="tablist">
            {[['maxim', labels.maxim], ['toz', labels.toz], ['orz', labels.orz]].map(([value, label]) => (
              <button type="button" role="tab" aria-selected={kind === value} className={kind === value ? 'is-active' : ''} onClick={() => setKind(value)} key={value}>{label}</button>
            ))}
          </div>
          <label className="serial-input"><span>{labels.label}</span><div><Search size={20} /><input value={input} onChange={(event) => setInput(event.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder={labels.placeholder} /></div></label>
          {kind === 'maxim' && Number.isFinite(number) && number < 10000 && <p className="serial-caution"><Info size={16} />{labels.resetNote}</p>}
        </div>

        {Number.isFinite(number) && (
          <div className={result ? 'year-result has-result' : 'year-result'} aria-live="polite">
            <span>{resultTitle}</span><strong>{resultValue}</strong>
            {result?.record && <small>{labels.range}: {labels.serialShort} {result.record.min.toLocaleString()}–{result.record.max.toLocaleString()}</small>}
            {result?.evidence?.length > 0 && <small>{labels.nearest}: {result.evidence.map((point) => `${labels.serialShort}${point.number} (${point.year})`).join(' · ')}</small>}
            {searchUrl && <Link to={searchUrl}>{labels.records}<ArrowRight size={16} /></Link>}
          </div>
        )}
      </section>

      <section className="year-reference" aria-labelledby="reference-title">
        <div className="section-heading"><div><h2 id="reference-title">{kind === 'orz' ? labels.orzTimeline : labels.timeline}</h2></div><p>{kind === 'orz' ? labels.orzNote : labels.note}</p></div>

        {kind === 'orz' && orzStatus === 'loading' && <p className="archive-notice">{labels.loadingOrz}</p>}
        {kind === 'orz' && orzStatus === 'error' && <p className="archive-notice archive-notice--error">{labels.errorOrz}</p>}
        {kind === 'orz' && orzStatus === 'ready' && orzData.grouped.length === 0 && <p className="archive-notice">{labels.noOrz}</p>}

        {kind !== 'orz' && (
          <div className="year-table" role="table">
            {ranges.map((range) => <Link to={'/search?' + new URLSearchParams({ searchType: 'all', belongsTo, year: String(range.year) }).toString()} className={selectedYears.includes(range.year) ? 'year-table__row is-selected' : 'year-table__row'} key={range.year} role="row"><strong role="cell">{range.year}</strong><span role="cell">{labels.serialShort} {range.min.toLocaleString()}–{range.max.toLocaleString()}</span><ArrowRight size={16} /></Link>)}
          </div>
        )}

        {kind === 'orz' && orzStatus === 'ready' && orzData.grouped.length > 0 && (
          <div className="year-table year-table--orz" role="table">
            {orzData.grouped.map((group) => {
              const url = '/search?' + new URLSearchParams({ searchType: 'all', belongsTo: 'станок', manufacturer: 'ОР.З.', year: String(group.year) }).toString();
              return <Link to={url} className={selectedYears.includes(group.year) ? 'year-table__row is-selected' : 'year-table__row'} key={group.year} role="row"><strong role="cell">{group.year}</strong><span className="year-table__numbers" role="cell">{group.numbers.join(' · ')}</span><ArrowRight size={16} /></Link>;
            })}
          </div>
        )}
      </section>
    </main>
  );
}
